/**
 * Prüfschritt des Monats-Editors (Board „Änderungen prüfen“): wie viele Tage von der Automatik in
 * die Hand wechseln und wie viele schon von Hand gepflegt sind, sagt der Server
 * (`ari/ownership/impact`); einzeln genannt werden nur Tage aus dem geladenen Fenster. Ohne
 * Antwort des Servers wird nichts vorgemerkt.
 */
import type { AriPushBatchRange } from '@/shared/api';
import { problemText } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { MonthReviewBox, countDays, listDays } from './MonthReviewBox';
import type { ReviewDays } from './monthReview';
import { useOwnershipImpact } from './useOwnershipImpact';

interface Props {
  open: boolean;
  propertyId: string;
  apartmentName: string;
  title: string;
  ranges: AriPushBatchRange[];
  days: ReviewDays;
  saving: boolean;
  /** Satz, wenn das Vormerken scheiterte – der Dialog bleibt offen. */
  pushError?: string | null;
  onBack: () => void;
  onConfirm: () => void;
}

export function MonthReviewDialog({ open, propertyId, ranges, days, saving, ...props }: Props) {
  const effect = useOwnershipImpact(propertyId, ranges, open);
  const { impact, problem, owning, ready } = effect;
  return (
    <Dialog open={open} onOpenChange={(value) => !value && !saving && props.onBack()}>
      <DialogContent showClose={!saving}>
        <DialogHeader>
          <p className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
            Änderungen prüfen · {props.apartmentName}
          </p>
          <DialogTitle className="font-serif text-2xl font-bold">{props.title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          {effect.loading && (
            <p role="status" className="text-ink-soft text-sm">
              Auswirkung wird geladen…
            </p>
          )}
          {effect.offline && (
            <p role="status" className="text-ink-soft text-sm">
              Keine Verbindung – die Auswirkung lädt, sobald das Netz zurück ist.
            </p>
          )}
          {impact.isError && (
            <div role="alert" className="text-sealwax flex flex-wrap items-center gap-2 text-sm">
              <span className="grow">
                {problemText(
                  problem,
                  'Die Auswirkung auf die Preisautomatik konnte nicht geladen werden.',
                )}{' '}
                Es wird nichts vorgemerkt.
              </span>
              <Button
                variant="ghost"
                className="min-h-11"
                onClick={() => void impact.refetch()}
                loading={impact.isFetching}
              >
                Erneut laden
              </Button>
            </div>
          )}
          {effect.rateDays > 0 && (
            <MonthReviewBox
              tone="amber"
              title={`Preis: ${countDays(effect.rateDays)} ${effect.rateDays === 1 ? 'wechselt' : 'wechseln'} von Automatik zu manuell`}
            >
              {listDays(days.rate)}Die Preisautomatik ändert diese Preise danach nicht mehr, bis du
              sie wieder freigibst.
            </MonthReviewBox>
          )}
          {effect.minStayDays > 0 && (
            <MonthReviewBox
              tone="amber"
              title={`Mindestnächte: ${countDays(effect.minStayDays)} ${effect.minStayDays === 1 ? 'wechselt' : 'wechseln'} von Automatik zu manuell`}
            >
              {listDays(days.minStay)}Die Automatik ändert diese Mindestnächte danach nicht mehr.
            </MonthReviewBox>
          )}
          {effect.alreadyManual > 0 && (
            <MonthReviewBox
              tone="paper"
              title={`${countDays(effect.alreadyManual)} ${effect.alreadyManual === 1 ? 'wird' : 'werden'} schon von Hand gepflegt`}
            >
              {listDays(days.alreadyManual)}Preis oder Mindestnächte sind dort schon von Hand
              gesetzt; sie bekommen die neuen Werte.
            </MonthReviewBox>
          )}
          {ready && !owning && (
            <p className="text-ink-soft text-sm">
              Diese Änderung übernimmt keinen Preis und keine Mindestnächte aus der Automatik.
            </p>
          )}
          {props.pushError && (
            <p
              role="alert"
              className="bg-sealwax-tint text-sealwax rounded-control p-3 text-sm font-semibold"
            >
              {props.pushError}
            </p>
          )}
          <p className="text-ink-soft text-[13px]">Leere Felder bleiben, wie sie sind.</p>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            className="rounded-pill h-12"
            onClick={props.onBack}
            disabled={saving}
          >
            Zurück
          </Button>
          <Button
            variant="brand"
            className="rounded-pill h-12 grow"
            onClick={props.onConfirm}
            disabled={!ready}
            loading={saving}
          >
            Vormerken und übertragen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
